import Phaser from 'phaser';
import { MAP_BORDER_SIZE, TILE_SIZE, WORLD_TILES } from '@shared/constants';

interface GridFlags {
  showGrid: boolean;
  showGridCoords: boolean;
  showBorderBlocks: boolean;
  showFloorBlocks: boolean;
}

interface GridState {
  gridContainer: Phaser.GameObjects.Container | null;
  gridGraphics: Phaser.GameObjects.Graphics | null;
  gridLabelContainer: Phaser.GameObjects.Container | null;
  gridLabelPool: Phaser.GameObjects.Text[];
  activeGridLabelCount: number;
  lastGridTileStartX: number | null;
  lastGridTileStartY: number | null;
  blockedTiles: Set<string>;
  playerTileX: number | null;
  playerTileY: number | null;
  localPlayerWorldX: number;
  localPlayerWorldY: number;
}

export class MapDebugGridSystem {
  private scene: Phaser.Scene;
  private state: GridState;
  private flags: GridFlags;
  private mapContainer: Phaser.GameObjects.Container | null = null;

  constructor(scene: Phaser.Scene, flags: GridFlags, blockedTiles: Set<string>) {
    this.scene = scene;
    this.flags = flags;
    this.state = {
      gridContainer: null,
      gridGraphics: null,
      gridLabelContainer: null,
      gridLabelPool: [],
      activeGridLabelCount: 0,
      lastGridTileStartX: null,
      lastGridTileStartY: null,
      blockedTiles,
      playerTileX: null,
      playerTileY: null,
      localPlayerWorldX: 0,
      localPlayerWorldY: 0,
    };
  }

  setLocalPlayerWorldPosition(worldX: number, worldY: number): void {
    this.state.localPlayerWorldX = worldX;
    this.state.localPlayerWorldY = worldY;
  }

  setMapContainer(mapContainer: Phaser.GameObjects.Container | null): void {
    this.mapContainer = mapContainer;
  }

  updateFlags(flags: Partial<GridFlags>): void {
    this.flags = { ...this.flags, ...flags };
  }

  setBlockedTiles(blockedTiles: Set<string>): void {
    this.state.blockedTiles = blockedTiles;
  }

  setPlayerTile(tileX: number | null, tileY: number | null): void {
    this.state.playerTileX = tileX;
    this.state.playerTileY = tileY;
  }

  drawGrid(): void {
    const { showGrid, showGridCoords, showBorderBlocks, showFloorBlocks } = this.flags;
    const anyDebugVisible = showGrid || showGridCoords || showBorderBlocks || showFloorBlocks;

    if (!anyDebugVisible) {
      if (this.state.gridContainer) {
        this.state.gridContainer.setVisible(false);
      }
      return;
    }

    if (this.state.gridContainer) {
      this.state.gridContainer.destroy();
      this.state.gridContainer = null;
    }

    // AO-like: Crear gridContainer como hijo del mapContainer si está disponible
    if (this.mapContainer) {
      this.state.gridContainer = this.scene.add.container(0, 0).setDepth(-1);
      this.mapContainer.add(this.state.gridContainer);
    } else {
      this.state.gridContainer = this.scene.add.container(0, 0).setDepth(-1);
    }
    this.state.gridContainer.setVisible(true);

    this.state.gridGraphics = this.scene.add.graphics();
    this.state.gridContainer.add(this.state.gridGraphics);

    this.state.gridLabelContainer = this.scene.add.container(0, 0);
    this.state.gridContainer.add(this.state.gridLabelContainer);

    this.state.gridLabelPool = [];
    this.state.activeGridLabelCount = 0;
    this.state.lastGridTileStartX = null;
    this.state.lastGridTileStartY = null;

    this.updateGridWindow(true);
  }

  updateGridWindow(force: boolean = false): void {
    const { showGrid, showGridCoords, showBorderBlocks, showFloorBlocks } = this.flags;
    const anyDebugVisible = showGrid || showGridCoords || showBorderBlocks || showFloorBlocks;

    if (!anyDebugVisible) return;
    if (!this.state.gridContainer || !this.state.gridGraphics || !this.state.gridLabelContainer) return;

    const cam = this.scene.cameras.main;
    const camWidth = cam.width;
    const camHeight = cam.height;
    const tileSize = TILE_SIZE;

    // AO-like: Calcular tiles visibles basados en la posición del jugador local
    // El jugador está siempre en el centro de la pantalla en el sistema AO-like
    const margin = 5;
    
    // AO-like: Calcular qué tiles son visibles basados en la posición del jugador local
    // La grilla se dibuja en coordenadas de mundo dentro del mapContainer
    const playerWorldX = this.state.localPlayerWorldX;
    const playerWorldY = this.state.localPlayerWorldY;
    
    // Calcular el rango de tiles visibles alrededor del jugador
    const visibleWorldLeft = playerWorldX - camWidth / 2;
    const visibleWorldRight = playerWorldX + camWidth / 2;
    const visibleWorldTop = playerWorldY - camHeight / 2;
    const visibleWorldBottom = playerWorldY + camHeight / 2;
    
    const startGX = Math.max(0, Math.floor(visibleWorldLeft / tileSize) - margin);
    const startGY = Math.max(0, Math.floor(visibleWorldTop / tileSize) - margin);
    const endGX = Math.min(WORLD_TILES - 1, Math.ceil(visibleWorldRight / tileSize) + margin);
    const endGY = Math.min(WORLD_TILES - 1, Math.ceil(visibleWorldBottom / tileSize) + margin);

    if (!force && this.state.lastGridTileStartX === startGX && this.state.lastGridTileStartY === startGY) {
      return;
    }
    this.state.lastGridTileStartX = startGX;
    this.state.lastGridTileStartY = startGY;

    const g = this.state.gridGraphics;
    g.clear();
    this.state.activeGridLabelCount = 0;

    for (let gx = startGX; gx <= endGX; gx += 1) {
      for (let gy = startGY; gy <= endGY; gy += 1) {
        const isBorder =
          gx < MAP_BORDER_SIZE ||
          gx >= WORLD_TILES - MAP_BORDER_SIZE ||
          gy < MAP_BORDER_SIZE ||
          gy >= WORLD_TILES - MAP_BORDER_SIZE;

        const isBlockedByMap = this.state.blockedTiles?.has(`${gx},${gy}`) ?? false;
        const isFloorBlock = isBlockedByMap && !isBorder;

        const px = gx * tileSize;
        const py = gy * tileSize;

        if (showBorderBlocks && isBorder && isBlockedByMap) {
          g.fillStyle(0x333355, 0.5);
          g.fillRect(px, py, tileSize, tileSize);
          g.lineStyle(2, 0xff4444, 0.9);
          g.beginPath();
          g.moveTo(px + 4, py + 4);
          g.lineTo(px + tileSize - 4, py + tileSize - 4);
          g.moveTo(px + tileSize - 4, py + 4);
          g.lineTo(px + 4, py + tileSize - 4);
          g.strokePath();
        }

        if (showFloorBlocks && isFloorBlock) {
          g.fillStyle(0x552222, 0.5);
          g.fillRect(px, py, tileSize, tileSize);
          g.lineStyle(2, 0xff4444, 0.9);
          g.beginPath();
          g.moveTo(px + 4, py + 4);
          g.lineTo(px + tileSize - 4, py + tileSize - 4);
          g.moveTo(px + tileSize - 4, py + 4);
          g.lineTo(px + 4, py + tileSize - 4);
          g.strokePath();
        }
      }
    }

    if (showGrid) {
      g.lineStyle(1, 0x333333, 1);
      for (let gx = startGX; gx <= endGX + 1; gx += 1) {
        const x = gx * tileSize;
        g.moveTo(x, startGY * tileSize);
        g.lineTo(x, (endGY + 1) * tileSize);
      }
      for (let gy = startGY; gy <= endGY + 1; gy += 1) {
        const y = gy * tileSize;
        g.moveTo(startGX * tileSize, y);
        g.lineTo((endGX + 1) * tileSize, y);
      }
      g.strokePath();

      // Cruz central del mundo: columna + fila del medio (tile WORLD_TILES/2).
      // El mundo es impar → centro único; ayuda a ubicar la mitad del mapa.
      const mid = Math.floor(WORLD_TILES / 2);
      if (mid >= startGX && mid <= endGX) {
        const cx = mid * tileSize;
        g.fillStyle(0xffd700, 0.15);
        g.fillRect(cx, startGY * tileSize, tileSize, (endGY - startGY + 1) * tileSize);
      }
      if (mid >= startGY && mid <= endGY) {
        const cy = mid * tileSize;
        g.fillStyle(0xffd700, 0.15);
        g.fillRect(startGX * tileSize, cy, (endGX - startGX + 1) * tileSize, tileSize);
      }
      if (mid >= startGX && mid <= endGX && mid >= startGY && mid <= endGY) {
        g.lineStyle(3, 0xffd700, 0.9);
        g.strokeRect(mid * tileSize, mid * tileSize, tileSize, tileSize);
      }

      // Marcar el tile del jugador con un borde destacado
      if (this.state.playerTileX !== null && this.state.playerTileY !== null) {
        const playerTileX = this.state.playerTileX;
        const playerTileY = this.state.playerTileY;
        
        // Solo dibujar si el tile del jugador está en la ventana visible
        if (playerTileX >= startGX && playerTileX <= endGX && playerTileY >= startGY && playerTileY <= endGY) {
          const px = playerTileX * tileSize;
          const py = playerTileY * tileSize;
          
          // Dibujar un borde destacado alrededor del tile del jugador
          g.lineStyle(3, 0x00ff00, 0.8); // Verde brillante, semi-transparente
          g.strokeRect(px + 2, py + 2, tileSize - 4, tileSize - 4);
          
          // Dibujar un pequeño relleno semi-transparente para mayor visibilidad
          g.fillStyle(0x00ff00, 0.2);
          g.fillRect(px + 2, py + 2, tileSize - 4, tileSize - 4);
        }
      }
    }

    if (showGridCoords) {
      for (let gx = startGX; gx <= endGX; gx += 1) {
        for (let gy = startGY; gy <= endGY; gy += 1) {
          const labelX = gx * tileSize + tileSize / 2;
          const labelY = gy * tileSize + tileSize / 2;

          let label = this.state.gridLabelPool[this.state.activeGridLabelCount];
          if (!label) {
            label = this.scene.add
              .text(labelX, labelY, `${gx},${gy}`, {
                fontSize: '10px',
                color: '#aaaaaa',
              })
              .setOrigin(0.5)
              .setDepth(750)
              .setScrollFactor(0);
            this.state.gridLabelPool.push(label);
            this.state.gridLabelContainer.add(label);
          } else {
            label.setText(`${gx},${gy}`);
            label.setPosition(labelX, labelY);
            label.setVisible(true);
          }
          this.state.activeGridLabelCount += 1;
        }
      }

      for (let i = this.state.activeGridLabelCount; i < this.state.gridLabelPool.length; i += 1) {
        this.state.gridLabelPool[i].setVisible(false);
      }
    } else {
      this.state.gridLabelPool.forEach((lbl) => lbl.setVisible(false));
    }
  }
}













